import React from "react";
import {Box, Typography, Card, CardContent, Grid, List, ListItem, ListItemIcon, ListItemText, Chip, Divider} from "@mui/material";
import {
  Dashboard as DashboardIcon,
  Agriculture as FarmIcon,
  Water as PondIcon,
  Inventory as FeedIcon,
  Pets as AnimalIcon,
  People as UserIcon,
  Settings as SettingsIcon,
  Business as TenantIcon,
  Notifications as NotificationIcon,
  Analytics as AnalyticsIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import {useAuth} from "../../contexts/AuthContext";
import {UserRole} from "../../api/user";

const QuickReferenceGuide: React.FC = () => {
  const {user} = useAuth();

  const getRoleSpecificQuickTips = () => {
    switch (user?.role) {
      case UserRole.SUPER_ADMIN:
        return {
          title: "Super Administrator Quick Reference",
          shortcuts: [
            {icon: <TenantIcon />, action: "Manage Tenants", shortcut: "Tenants menu", description: "Create and manage tenant organizations"},
            {icon: <UserIcon />, action: "User Management", shortcut: "Users menu", description: "Manage all users across tenants"},
            {icon: <SettingsIcon />, action: "System Config", shortcut: "Configuration menu", description: "Configure global system settings"},
            {icon: <AnalyticsIcon />, action: "System Analytics", shortcut: "Dashboard", description: "View system-wide performance metrics"},
          ],
          tips: [
            "Regularly review system logs and performance metrics",
            "Monitor tenant status and usage patterns",
            "Keep user documentation and training materials updated",
            "Implement security best practices and regular audits",
          ],
          alerts: [
            {type: "warning", message: "Monitor system performance and resource usage"},
            {type: "info", message: "Regular backup of system configuration and data"},
            {type: "success", message: "Proactive issue identification and resolution"},
          ],
        };

      case UserRole.TENANT_ADMIN:
        return {
          title: "Tenant Administrator Quick Reference",
          shortcuts: [
            {icon: <FarmIcon />, action: "Manage Farms", shortcut: "Farms menu", description: "Create and manage farms"},
            {icon: <UserIcon />, action: "User Management", shortcut: "Users menu", description: "Manage users within organization"},
            {icon: <SettingsIcon />, action: "Org Config", shortcut: "Configuration menu", description: "Configure organization settings"},
            {icon: <AnalyticsIcon />, action: "Org Analytics", shortcut: "Dashboard", description: "View organization performance"},
          ],
          tips: [
            "Regular review of farm performance and health metrics",
            "Effective user training and support programs",
            "Proactive issue identification and resolution",
            "Continuous improvement of operational processes",
          ],
          alerts: [
            {type: "warning", message: "Monitor farm health and productivity metrics"},
            {type: "info", message: "Regular user training and support"},
            {type: "success", message: "Optimize farm operations and efficiency"},
          ],
        };

      case UserRole.FARMER_ADMIN:
        return {
          title: "Farmer Administrator Quick Reference",
          shortcuts: [
            {icon: <PondIcon />, action: "Manage Ponds", shortcut: "Ponds menu", description: "Create and manage ponds"},
            {icon: <AnimalIcon />, action: "Animal Groups", shortcut: "Animals menu", description: "Manage animal groups and health"},
            {icon: <FeedIcon />, action: "Feed Management", shortcut: "Feed menu", description: "Manage feed inventory and schedules"},
            {icon: <UserIcon />, action: "Staff Management", shortcut: "Users menu", description: "Manage supervisors and employees"},
          ],
          tips: [
            "Regular water quality monitoring and maintenance",
            "Consistent feeding schedules and protocols",
            "Proactive health monitoring and disease prevention",
            "Effective staff training and supervision",
          ],
          alerts: [
            {type: "warning", message: "Monitor water quality and animal health daily"},
            {type: "info", message: "Maintain consistent feeding schedules"},
            {type: "success", message: "Optimize feed efficiency and growth rates"},
          ],
        };

      case UserRole.SUPERVISOR:
        return {
          title: "Supervisor Quick Reference",
          shortcuts: [
            {icon: <PondIcon />, action: "Water Quality", shortcut: "Ponds > Select Pond", description: "Record water quality measurements"},
            {icon: <AnimalIcon />, action: "Animal Health", shortcut: "Animals > Select Group", description: "Track animal health and growth"},
            {icon: <FeedIcon />, action: "Feed Operations", shortcut: "Feed menu", description: "Manage feeding schedules"},
            {icon: <NotificationIcon />, action: "Alerts", shortcut: "Dashboard notifications", description: "Respond to alerts and issues"},
          ],
          tips: [
            "Consistent and accurate data recording",
            "Proactive issue identification and reporting",
            "Effective communication with management and staff",
            "Regular review and improvement of procedures",
          ],
          alerts: [
            {type: "warning", message: "Check water quality parameters daily"},
            {type: "info", message: "Monitor animal behavior and health indicators"},
            {type: "success", message: "Maintain accurate operational records"},
          ],
        };

      case UserRole.EMPLOYEE:
        return {
          title: "Employee Quick Reference",
          shortcuts: [
            {icon: <DashboardIcon />, action: "Daily Tasks", shortcut: "Dashboard", description: "View assigned tasks and schedules"},
            {icon: <PondIcon />, action: "Water Quality", shortcut: "Ponds > Record Data", description: "Record water quality measurements"},
            {icon: <FeedIcon />, action: "Feeding", shortcut: "Feed > Follow Schedule", description: "Follow feeding schedules"},
            {icon: <NotificationIcon />, action: "Report Issues", shortcut: "Dashboard alerts", description: "Report problems and concerns"},
          ],
          tips: [
            "Follow all safety procedures and protocols",
            "Maintain accurate and timely data records",
            "Report issues promptly and accurately",
            "Work effectively as part of the team",
          ],
          alerts: [
            {type: "warning", message: "Follow safety procedures at all times"},
            {type: "info", message: "Record data accurately and consistently"},
            {type: "success", message: "Report issues immediately when identified"},
          ],
        };

      default:
        return {
          title: "General Quick Reference",
          shortcuts: [
            {icon: <DashboardIcon />, action: "Dashboard", shortcut: "Home page", description: "View overview and key metrics"},
            {icon: <NotificationIcon />, action: "Notifications", shortcut: "Dashboard alerts", description: "Check for important alerts"},
            {icon: <SpeedIcon />, action: "Quick Actions", shortcut: "Various menus", description: "Access common tasks quickly"},
          ],
          tips: [
            "Start with the dashboard to understand current status",
            "Check notifications regularly for important updates",
            "Use the help section for guidance on specific features",
            "Contact your administrator for additional support",
          ],
          alerts: [
            {type: "info", message: "Explore the dashboard to understand your role"},
            {type: "success", message: "Use help resources for guidance and support"},
          ],
        };
    }
  };

  const quickTips = getRoleSpecificQuickTips();

  const getAlertIcon = (type: string) => {
    switch (type) {
      case "warning":
        return <WarningIcon color="warning" />;
      case "info":
        return <InfoIcon color="info" />;
      case "success":
        return <CheckIcon color="success" />;
      default:
        return <InfoIcon color="info" />;
    }
  };

  return (
    <Box sx={{mt: 4}}>
      <Typography variant="h5" gutterBottom sx={{display: "flex", alignItems: "center", gap: 1}}>
        <SpeedIcon color="primary" />
        {quickTips.title}
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quick Shortcuts
              </Typography>
              <List dense>
                {quickTips.shortcuts.map((shortcut, index) => (
                  <ListItem key={index} sx={{px: 0}}>
                    <ListItemIcon sx={{minWidth: 40}}>{shortcut.icon}</ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                          <Typography variant="subtitle2" fontWeight="medium">
                            {shortcut.action}
                          </Typography>
                          <Chip label={shortcut.shortcut} size="small" variant="outlined" />
                        </Box>
                      }
                      secondary={shortcut.description}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Best Practices
              </Typography>
              <List dense>
                {quickTips.tips.map((tip, index) => (
                  <ListItem key={index} sx={{px: 0}}>
                    <ListItemIcon sx={{minWidth: 40}}>
                      <CheckIcon color="success" />
                    </ListItemIcon>
                    <ListItemText primary={tip} />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Important Reminders
              </Typography>
              <Box sx={{display: "flex", flexDirection: "column", gap: 2}}>
                {quickTips.alerts.map((alert, index) => (
                  <Box key={index} sx={{display: "flex", alignItems: "center", gap: 2}}>
                    {getAlertIcon(alert.type)}
                    <Typography variant="body2" color="text.secondary">
                      {alert.message}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default QuickReferenceGuide;
